import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BaseDashboard } from '../../models/dashboard/BaseDashboard';
import { FormSearch } from '../../models/dashboard/FormStatus';
import { UtilityService } from '../utility.service';
import { QuickInvoiceFormView, QuickInvoicePaymentView } from 'app/models/miscellaneous-billing/QuickInvoiceFormView';
import { setupAttachmentFormRequest } from './attachment-form.utils';

@Injectable({
	providedIn: 'root',
})
export class QuickInvoiceFromService {
	private readonly urlPath = `${environment.apiUrl}miscellaneous-billing/clients/`;

	constructor(private httpClient: HttpClient, private utilityService: UtilityService) {}

	createForm(clientId: string, request: any): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/quick-invoice-form`, setupAttachmentFormRequest(request))
			.pipe();
	}

	updateForm(clientId: string, id: string, request: any): Observable<any> {
		return this.httpClient
			.put<any>(`${this.urlPath}${clientId}/quick-invoice-form/${id}`, setupAttachmentFormRequest(request))
			.pipe();
	}

	getForm(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/quick-invoice-form/${id}`).pipe();
	}

	rejectForm(clientId: string, id: string, rejectionReason: { reason: string; workflowId: number }): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/quick-invoice-form/${id}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, id: string, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/quick-invoice-form/${id}/approve`, {
				userId,
			})
			.pipe();
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		let url = `${this.urlPath}${clientId}/quick-invoice-form/users/${userId}?${this.utilityService.objectToQueryString(
			search
		)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	sendBack(clientId: string, id: string): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/quick-invoice-form/${id}/review`, {}).pipe();
	}
	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(`${this.urlPath}${clientId}/quick-invoice-form/${formId}/docs/${docId}`);
	}
	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest('POST', `${this.urlPath}${clientId}/quick-invoice-form/${id}/docs`, formData, {
			reportProgress: true,
			responseType: 'json',
		});
		return this.httpClient.request(req);
	}

	deleteForm(clientId: string, id: string) {
		return this.httpClient.delete(`${this.urlPath}${clientId}/quick-invoice-form/${id}`).pipe();
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<QuickInvoiceFormView> {
		return this.httpClient
			.post<QuickInvoiceFormView>(`${this.urlPath}${clientId}/quick-invoice-form/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	search(clientId: string): Observable<QuickInvoiceFormView[]> {
		return this.httpClient.get<QuickInvoiceFormView[]>(`${this.urlPath}${clientId}/quick-invoice-form/search`).pipe();
	}

	generateInvoice(clientId: string, id: string): Observable<any> {
		let url = `${this.urlPath}${clientId}/quick-invoice-form/invoice/${id}`;
		return this.httpClient.get<any>(url).pipe();
	}

	getPaymentHistory(clientId: string, id: any): Observable<QuickInvoicePaymentView[]> {
		return this.httpClient
			.get<QuickInvoicePaymentView[]>(`${this.urlPath}${clientId}/quick-invoice-form/item/${id}`)
			.pipe();
	}

	itemPayment(clientId: string, request: QuickInvoicePaymentView): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/quick-invoice-form/item-payment`, request).pipe();
	}
	getRecurringForms(clientId: string): Observable<any[]> {
		return this.httpClient.get<any[]>(`${this.urlPath}${clientId}/quick-invoice-form/recurring-forms`).pipe();
	}

	export(clientId: string) {
		return this.httpClient
			.get(`${this.urlPath}${clientId}/quick-invoice-form/export`, {
				responseType: 'blob',
			})
			.pipe();
	}

	getActiveForms(clientId: string): Observable<QuickInvoiceFormView[]> {
		return this.httpClient
			.get<QuickInvoiceFormView[]>(`${this.urlPath}${clientId}/quick-invoice-form/activeinvoices`)
			.pipe();
	}

	// Attachment methods
	uploadAttachment(clientId: string, invoiceId: string, file: File, attachmentView: any): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		formData.append('attachmentView', JSON.stringify(attachmentView));

		const req = new HttpRequest(
			'POST',
			`${this.urlPath}${clientId}/quick-invoice-form/${invoiceId}/attachments`,
			formData,
			{
				reportProgress: true,
				responseType: 'json',
			}
		);
		return this.httpClient.request(req);
	}

	updateAttachment(clientId: string, invoiceId: string, attachmentId: string, attachmentView: any): Observable<any> {
		return this.httpClient
			.put<any>(
				`${this.urlPath}${clientId}/quick-invoice-form/${invoiceId}/attachments/${attachmentId}`,
				attachmentView
			)
			.pipe();
	}

	deleteAttachment(clientId: string, invoiceId: string, attachmentId: string): Observable<any> {
		return this.httpClient
			.delete<any>(`${this.urlPath}${clientId}/quick-invoice-form/${invoiceId}/attachments/${attachmentId}`)
			.pipe();
	}

	getTransactionStatus(clientId: string, paymentId: number): Observable<any> {
		return this.httpClient
			.get<any>(`${this.urlPath}${clientId}/quick-invoice-form/payment/${paymentId}/transaction-status`)
			.pipe();
	}

	refundPayment(
		clientId: string,
		paymentId: number,
		request: { amount: number; refundType: string; reason: string }
	): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/quick-invoice-form/payment/${paymentId}/refund`, request)
			.pipe();
	}

	voidPayment(clientId: string, paymentId: number, request: { reason: string }): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/quick-invoice-form/payment/${paymentId}/void`, request)
			.pipe();
	}
}
